import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { 
  CreditCard, ExternalLink, Check, AlertCircle, User, Mail, Phone, 
  MapPin, Calendar, Save, Loader2, CheckCircle, XCircle, FileText, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export default function DriverSettings() {
  const { api, user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [documentStatus, setDocumentStatus] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(true);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        city: user.city || ''
      });
    }
    fetchStripeStatus();
    fetchDocumentStatus();
    
    // Handle Stripe return
    if (searchParams.get('stripe_success') === 'true') {
      toast.success('Stripe onboarding completed! Your account is being verified.');
      fetchStripeStatus();
    } else if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe onboarding.');
      handleStripeOnboard();
    }
  }, [user, searchParams]);

  const fetchStripeStatus = async () => {
    setStripeLoading(true);
    try {
      const response = await api.get('/drivers/stripe-connect/status');
      setStripeStatus(response.data);
    } catch (error) {
      console.log('Stripe status not available');
      setStripeStatus(null);
    } finally {
      setStripeLoading(false);
    }
  };

  const fetchDocumentStatus = async () => {
    setDocumentLoading(true);
    try {
      const response = await api.get('/drivers/documents/status');
      setDocumentStatus(response.data);
    } catch (error) {
      console.log('Document status not available');
      setDocumentStatus(null);
    } finally {
      setDocumentLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/users/profile', {
        name: profile.name,
        phone: profile.phone,
        city: profile.city
      });
      toast.success('Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleStripeOnboard = async () => {
    setOnboardingLoading(true);
    try {
      const response = await api.post('/drivers/stripe-connect/onboard');
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe onboarding');
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const response = await api.get('/drivers/stripe-connect/dashboard');
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStripeStatusDisplay = () => {
    if (stripeLoading) {
      return (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Checking status...</span>
        </div>
      );
    }

    if (!stripeStatus?.connected) {
      return (
        <div className="bg-amber-50 p-4 rounded-xl" data-testid="stripe-not-connected">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-amber-700">Stripe Not Connected</p>
              <p className="text-sm text-amber-600">Connect your Stripe account to receive delivery earnings</p>
            </div>
            <Button 
              onClick={handleStripeOnboard} 
              className="btn-primary"
              disabled={onboardingLoading}
              data-testid="connect-stripe-btn"
            >
              {onboardingLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect Stripe
                </>
              )}
            </Button>
          </div>
        </div>
      );
    }

    if (!stripeStatus.details_submitted) {
      return (
        <div className="bg-orange-50 p-4 rounded-xl" data-testid="stripe-incomplete">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-semibold text-orange-700">Onboarding Incomplete</p>
                <p className="text-sm text-orange-600">Please complete your Stripe verification to receive payouts</p>
              </div>
            </div>
            <Button 
              onClick={handleStripeOnboard}
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-100"
              disabled={onboardingLoading}
            >
              {onboardingLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Complete Onboarding
                </>
              )}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4" data-testid="stripe-connected">
        <div className="bg-green-50 p-4 rounded-xl">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-semibold text-green-700">Stripe Connected</p>
              <p className="text-sm text-green-600">Your account is ready to receive payouts</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              {stripeStatus.payouts_enabled ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm font-medium text-slate-700">Payouts</span>
            </div>
            <p className="text-xs text-slate-500">
              {stripeStatus.payouts_enabled ? 'Enabled' : 'Pending verification'}
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              {stripeStatus.charges_enabled ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm font-medium text-slate-700">Payments</span>
            </div>
            <p className="text-xs text-slate-500">
              {stripeStatus.charges_enabled ? 'Enabled' : 'Pending verification'}
            </p>
          </div>
        </div>
        
        <Button 
          onClick={handleStripeDashboard}
          variant="outline"
          className="w-full"
          data-testid="stripe-dashboard-btn"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Open Stripe Dashboard
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="driver-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and payout settings</p>
      </div>

      {/* Stripe Connect Card - Top Priority */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-orange-500" />
            Payout Settings
          </CardTitle>
          <CardDescription>Connect your Stripe account to receive your delivery earnings</CardDescription>
        </CardHeader>
        <CardContent>
          {getStripeStatusDisplay()}
        </CardContent>
      </Card>

      {/* Document Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" />
            Document Status
          </CardTitle>
          <CardDescription>Your verified documents and expiry dates</CardDescription>
        </CardHeader>
        <CardContent>
          {documentLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading documents...</span>
            </div>
          ) : documentStatus?.documents?.length > 0 ? (
            <div className="space-y-3">
              {documentStatus.has_expiring && (
                <div className="bg-amber-50 p-3 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-700">Some documents are expiring soon. Please upload new versions via the web portal.</p>
                </div>
              )}
              {documentStatus.has_expired && (
                <div className="bg-red-50 p-3 rounded-lg flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-700">Some documents have expired. Please upload new versions to continue delivering.</p>
                </div>
              )}
              <div className="grid gap-3">
                {documentStatus.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        doc.status === 'expired' ? 'bg-red-100' :
                        doc.status === 'expiring_soon' ? 'bg-amber-100' :
                        'bg-green-100'
                      }`}>
                        {doc.status === 'expired' ? (
                          <XCircle className="w-5 h-5 text-red-600" />
                        ) : doc.status === 'expiring_soon' ? (
                          <Clock className="w-5 h-5 text-amber-600" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{doc.display_name}</p>
                        <p className="text-sm text-slate-500">
                          {doc.expiry_date ? `Expires: ${new Date(doc.expiry_date).toLocaleDateString('en-GB')}` : 'No expiry'}
                        </p>
                      </div>
                    </div>
                    <Badge className={
                      doc.status === 'expired' ? 'bg-red-100 text-red-700' :
                      doc.status === 'expiring_soon' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }>
                      {doc.status === 'expired' ? 'Expired' :
                       doc.status === 'expiring_soon' ? `${doc.days_until_expiry} days` :
                       'Valid'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto text-slate-200 mb-3" />
              <p>No documents uploaded yet</p>
              <p className="text-sm">Upload documents via the web portal</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>Update your account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your name"
                data-testid="driver-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                disabled
                className="bg-slate-50"
              />
              <p className="text-xs text-slate-500">Email cannot be changed</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Enter your phone"
                data-testid="driver-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={profile.city}
                onChange={(e) => setProfile(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Enter your city"
                data-testid="driver-city-input"
              />
            </div>
          </div>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={loading} className="btn-primary" data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Role</p>
                <p className="font-semibold text-slate-900">Driver</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Member Since</p>
                <p className="font-semibold text-slate-900">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
